import React, { useEffect } from 'react';
import cx from 'classnames';
import { RawHtml } from '@unisporkal/utilities';
import { useTranslations } from '@unisporkal/localization';
import { useTheme } from '@unisporkal/themes';
import { useExperience } from '@unisporkal/experiences';
import styles from './LimitedUseProductsCallout.module.scss';
import { limitedUseTrackingSkus } from '../../../support/tracking.ts';
import acquisitionTypes from '../../../support/acquisitionTypes.ts';
import useSelectedLimitedUseProduct from '../../../hooks/stateful/useSelectedLimitedUseProduct';
import { useAcquisitionType } from '../../../hooks/stateful/useAcquisitionType';
import { useAsset } from '../../../hooks/stateful/useAsset';
import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex.ts';
import useOnLoad from '../../../hooks/queries/useOnLoad';

const isModifiedAsset = (acquisitionType) =>
  acquisitionType === acquisitionTypes.MODIFIED_LICENSE ||
  acquisitionType === acquisitionTypes.MODIFIED_CART_CONFIRMATION;

const LimitedUseProductsCallout = () => {
  const t = useTranslations();
  const theme = useTheme();
  const themeLink = theme.link || {};
  const limitedUseExperience = useExperience('GettyLimitedUseProducts');
  const { acquisitionType, pushAcquisitionView } = useAcquisitionType();
  const { asset } = useAsset();
  const { selectedSize } = useSelectedIndex();
  const { setSelectedLimitedUseProduct } = useSelectedLimitedUseProduct();
  const { response: { limitedUseProducts } = {} } = useOnLoad();

  const limitedUseAvailable =
    limitedUseExperience.isActive() &&
    asset.isRoyaltyFree() &&
    !asset.isEditorial() &&
    limitedUseProducts?.length > 0 &&
    selectedSize?.license !== 'commercial-market-freeze';

  useEffect(() => {
    if (window?.tracking_data && limitedUseProducts?.length > 0) {
      window.tracking_data.merchandising_units = ['limited_use_merch-shown'];
      const existingOptions =
        window.tracking_data.licensing_options_shown?.split(',') || [];
      limitedUseProducts?.forEach((product) => {
        const productSku = limitedUseTrackingSkus[product.usage];
        if (productSku && !existingOptions.includes(productSku)) {
          existingOptions.push(productSku);
        }
      });
      window.tracking_data.licensing_options_shown = existingOptions.join(',');
    }
  }, [limitedUseAvailable]);

  const limitedUseProductsString = () => {
    const locProductOrder = [
      'SocialMedia',
      'SocialMediaPlusOneDigital',
      'Internal',
    ];
    const products = limitedUseProducts.map((product) => product.usage);
    const orderedProducts = products.sort(
      (a, b) => locProductOrder.indexOf(a) - locProductOrder.indexOf(b)
    );
    const keys = {
      SocialMediaSocialMediaPlusOneDigitalInternal:
        'limited_use_cta_social_digital_internal',
      SocialMediaInternal: 'limited_use_cta_social_internal',
      SocialMediaPlusOneDigitalInternal: 'limited_use_cta_digital_internal',
      SocialMediaSocialMediaPlusOneDigital: 'limited_use_cta_social_digital',
      Internal: 'limited_use_cta_internal',
      SocialMedia: 'limited_use_cta_social',
      SocialMediaPlusOneDigital: 'limited_use_cta_digital',
    };
    const key = keys[products.join('')];
    const links = orderedProducts.map((product) => ({
      href: '#',
      'data-id': product,
    }));
    return t(key, { links });
  };

  const handleClick = (event) => {
    const product = event.target.dataset.id;
    if (isModifiedAsset(acquisitionType)) {
      pushAcquisitionView(acquisitionTypes.MODIFIED_LIMITED_USE_PRODUCTS);
    } else {
      pushAcquisitionView(acquisitionTypes.LIMITED_USE_PRODUCTS);
    }

    setSelectedLimitedUseProduct(product);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleClick();
    }
  };

  if (limitedUseAvailable) {
    return (
      <section
        className={cx(styles.container, themeLink.textLink)}
        onClick={handleClick}
        onKeyDown={handleKeyPress}
        data-testid="limited-use-products-callout-container"
        role="button"
        tabIndex={0}
      >
        <RawHtml>{limitedUseProductsString()}</RawHtml>
      </section>
    );
  }
  return null;
};

export default LimitedUseProductsCallout;
